import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingDurbanvillePaintersAltPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Durbanville Painters - Premium Painting Services | Residential & Commercial Experts',
    description: 'Premium painting services in Durbanville, Bellville, and Northern Cape Town. Expert interior, exterior, and commercial painting with over 14 years of experience. Quality guaranteed, competitive rates, and exceptional customer service.',
    keywords: 'Durbanville painters, premium painting services, house painters, commercial painting Durbanville, interior painting, exterior painting, Northern Suburbs painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Durbanville Painters Premium',
          description: 'Premium painting services for residential and commercial properties',
          address: 'Durbanville, Cape Town',
          priceRange: '$$',
          serviceArea: ['Durbanville', 'Bellville', 'Northern Suburbs', 'Tygervalley', 'Brackenfell']
        }),
        generateServiceSchema({
          name: 'Premium Painting Services',
          description: 'High-quality interior, exterior, and commercial painting',
          provider: 'Durbanville Painters Premium',
          areaServed: ['Durbanville', 'Northern Suburbs'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Premium Services']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Durbanville Painters Premium",
    location: "Durbanville, Cape Town",
    website: "https://durbanville-painters.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/5bdca690c9c5029384411d4f21904029.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=luxury%20durbanville%20home%20exterior%20premium%20painting%20finish%20elegant%20design%20manicured%20landscaping%20professional%20quality%20workmanship%20sophisticated%20color%20scheme&width=800&height=600&seq=gallery-dpa1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=high%20end%20interior%20room%20painting%20durbanville%20home%20premium%20finishes%20elegant%20decor%20sophisticated%20color%20palette%20professional%20application%20luxury%20living&width=800&height=600&seq=gallery-dpa2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20office%20painting%20durbanville%20business%20district%20professional%20environment%20modern%20workspace%20premium%20quality%20finish%20corporate%20setting&width=800&height=600&seq=gallery-dpa3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=premium%20exterior%20house%20painting%20durbanville%20residential%20area%20high%20quality%20materials%20professional%20application%20beautiful%20suburban%20setting%20modern%20home&width=800&height=600&seq=gallery-dpa4&orientation=landscape"
    ],
    services: [
      {
        title: "Premium Interior Painting",
        description: "Elevate your Durbanville home with our premium interior painting services. We specialize in creating sophisticated, luxurious living spaces using the finest quality paints and advanced application techniques. Our attention to detail and commitment to excellence ensures every room becomes a masterpiece that enhances your lifestyle and property value.",
        features: ["Luxury living areas and entertainment spaces", "Master bedrooms and guest suites", "Gourmet kitchens and butler's pantries", "Spa-style bathrooms and powder rooms", "Home offices and library spaces", "Wine cellars and tasting rooms", "Custom color matching and design", "Premium paint brand selection", "Decorative finishes and techniques", "Wallpaper installation and removal", "Crown molding and trim work", "Eco-luxury paint options"]
      },
      {
        title: "Premium Exterior Painting",
        description: "Transform your property's exterior with our premium painting services designed to withstand Durbanville's climate while creating stunning curb appeal. We use advanced weather-resistant coatings and meticulous application processes to ensure your home's exterior remains beautiful and protected for years to come.",
        features: ["Complete luxury home exteriors", "Heritage and character home restoration", "Modern architectural painting", "Premium boundary walls and gates", "Designer garage doors and entries", "Architectural detail highlighting", "Multi-story and complex structures", "Specialized surface treatments", "Premium wood staining and sealing", "Metal surface restoration", "Comprehensive weatherproofing", "Color consultation and planning"]
      },
      {
        title: "Commercial & Office Painting",
        description: "Professional commercial painting services for Durbanville businesses, offices, and commercial properties. We understand that your business environment reflects your brand and professionalism. Our team delivers exceptional results with minimal disruption to your operations, ensuring your workspace looks polished and inviting.",
        features: ["Corporate offices and headquarters", "Medical and dental practices", "Retail stores and showrooms", "Restaurants and hospitality venues", "Professional service offices", "Educational facilities", "Healthcare facilities", "Industrial and warehouse spaces", "Strata and body corporate", "After-hours scheduling available", "Brand color matching", "Commercial grade materials"]
      }
    ],
    serviceAreas: ["Durbanville", "Bellville", "Tygervalley", "Brackenfell", "Plattekloof", "Welgemoed", "Sonstraal Heights", "Durbanville Hills", "De Bron", "Vierlanden", "Eversdal", "Aurora", "Northern Suburbs"],
    about: "Durbanville Painters Premium is an established painting company specializing in high-quality residential and commercial painting services throughout Durbanville and the Northern Suburbs of Cape Town. With over 14 years of experience in premium painting solutions, we have earned a reputation for exceptional craftsmanship, personalized service, and unwavering attention to detail. Our commitment to excellence has made us the preferred choice for discerning homeowners and businesses seeking superior painting results.",
    aboutExtended: [
      "Founded in 2009 with a vision to provide premium painting services that exceed expectations, our company has consistently delivered outstanding results for hundreds of satisfied clients throughout Durbanville and surrounding areas. We specialize in both residential and commercial projects, from elegant family homes to sophisticated business environments. Our expertise spans traditional and contemporary architectural styles, ensuring we can enhance any property with appropriate techniques and finishes.",
      "Our team consists of highly skilled, experienced painters who share our passion for quality and attention to detail. Each team member undergoes rigorous training in advanced painting techniques, color theory, and customer service excellence. We maintain strict quality standards and continuously invest in professional development to stay current with industry innovations, new products, and best practices. Our painters are not just skilled technicians – they are craftspeople dedicated to transforming spaces into works of art.",
      "We maintain exclusive partnerships with premium paint manufacturers and specialty product suppliers to ensure access to the finest materials available. Our selection includes luxury paint brands, eco-friendly options, specialized coatings, and custom color matching services. We carefully evaluate each product for quality, durability, environmental impact, and suitability for Durbanville's specific climate conditions. This commitment to premium materials ensures our clients receive lasting value and exceptional results.",
      "Our comprehensive approach begins with detailed consultation to understand your vision, requirements, and expectations. We provide expert color and finish recommendations, develop detailed project plans, and execute with meticulous attention to every detail. Our project management ensures timely completion, clear communication, and minimal disruption to your daily routine. We take pride in our clean work practices, thorough preparation, and complete cleanup upon project completion.",
      "Customer satisfaction is our highest priority. We believe every project is an opportunity to exceed expectations and build lasting relationships with our clients. Our detailed preparation process, skilled application techniques, and comprehensive quality control ensure exceptional results that stand the test of time. We stand behind our work with comprehensive warranties and remain available for ongoing support and maintenance services.",
      "As a local Durbanville business, we are deeply committed to our community and take pride in contributing to the beauty and value of properties throughout the area. We support local suppliers when possible, employ local residents, and maintain the highest standards of professionalism and integrity in all our business practices. When you choose Durbanville Painters Premium, you're partnering with a company that cares about quality, community, and your complete satisfaction."
    ],
    ourApproach: [
      "Comprehensive consultation and design planning",
      "Expert color and finish recommendations",
      "Detailed project planning and scheduling",
      "Premium materials and product selection",
      "Thorough surface preparation and repair",
      "Advanced application techniques and quality control",
      "Clean, organized, and respectful work practices",
      "Regular communication and progress updates",
      "Meticulous cleanup and site restoration",
      "Final inspection and quality assurance",
      "Comprehensive warranty and guarantee",
      "Ongoing customer support and maintenance"
    ],
    whyChoose: [
      "14+ years of premium painting experience",
      "Hundreds of satisfied Durbanville clients",
      "Licensed, insured, and certified",
      "Skilled and experienced painting team",
      "Premium quality materials exclusively",
      "Competitive pricing with transparent quotes",
      "Free detailed consultations and estimates",
      "Comprehensive workmanship warranties",
      "Outstanding local reputation",
      "Specialized Northern Suburbs expertise",
      "Personalized customer service approach",
      "Environmentally responsible practices",
      "Flexible scheduling and project management",
      "Post-project support and maintenance"
    ],
    testimonials: [
      {
        name: "Jennifer Smith",
        rating: 5,
        text: "Durbanville Painters Premium exceeded our expectations in every way! Their attention to detail is incredible and the quality of work is absolutely outstanding. They painted our entire home and the results are stunning. Professional, reliable, and worth every penny. Highly recommend!"
      },
      {
        name: "Robert Wilson",
        rating: 5,
        text: "We hired them for our commercial offices in Durbanville and couldn't be happier. They worked around our business schedule, completed everything on time, and the quality is exceptional. Very professional team and excellent project management throughout."
      },
      {
        name: "Angela van Zyl",
        rating: 5,
        text: "Simply the best painters in Durbanville! They helped us choose perfect colors, prepared everything meticulously, and delivered a flawless finish. The team was courteous, clean, and professional. Our home looks absolutely beautiful now!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Durbanville Painters Premium</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Durbanville Painters Premium</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We provide premium painting services throughout Durbanville and the Northern Suburbs:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Premium Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Exceptional painting solutions for discerning homeowners and businesses in Durbanville.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Durbanville & Northern Suburbs</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5:30PM</p>
                    <p className="text-gray-600">Sat: 8AM-2PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>•</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}